import { useContext } from "react";
import { SessionContext } from "./SessionProvider";

/**
 * The session, in the shape the screens already expect.
 *
 * **The shape is unchanged from the Auth0 version on purpose** — that is what
 * made `patreon-direct-auth` task 9 a change to three files rather than to
 * every screen. `isAuthenticated`, `isLoading`, `sub`, `signIn` and `signOut`
 * mean exactly what they meant before.
 *
 * ### What is no longer here, and where it went
 *
 * `name`, `email` and `picture` used to come from Auth0's `user` object, which
 * the browser held. There is no such object now: the session is an `httpOnly`
 * cookie carrying a `sub` and nothing else, which is the property the whole
 * design exists for. All three now come from `GET /me` through
 * `useCreator()` — one authority for the profile instead of two that could
 * disagree, which is a small improvement smuggled in by necessity.
 *
 * They are still declared here, always `undefined`, so the `??` fallbacks at
 * the call sites keep compiling and keep reading the way they did. Task 12
 * removes them along with the last of Auth0.
 */
export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    /*
     * Loud, not lenient. `useAuth0` outside a provider quietly reported
     * `isLoading: true` forever, and that leniency is what let `App.test.tsx`
     * silently exercise only the signed-out path for as long as it did.
     */
    throw new Error("useSession must be used inside a <SessionProvider>");
  }

  const { session, signIn, signOut } = context;

  return {
    isAuthenticated: session.status === "signed-in",
    isLoading: session.status === "loading",
    sub: session.status === "signed-in" ? session.sub : undefined,
    /** @deprecated Read it from `useCreator().me`. Removed at task 12. */
    name: undefined as string | undefined,
    /** @deprecated Read it from `useCreator().me`. Removed at task 12. */
    email: undefined as string | undefined,
    /** @deprecated Read it from `useCreator().me`. Removed at task 12. */
    picture: undefined as string | undefined,
    signIn,
    signOut,
  };
}
